'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

interface DeferredProps {
  /** Whether the underlying condition (a mutation in flight, an auth check,
   *  …) currently holds. Defaults to `true`, matching the simplest usage
   *  where the caller only mounts `<Deferred>` while its own condition is
   *  true (`{isLoading && <Deferred>{spinner}</Deferred>}`) — that pattern
   *  gets the `delay` guarantee for free. Pass `show` explicitly (keeping
   *  `<Deferred>` mounted across the whole lifetime of the surrounding
   *  component) to also get the `minVisible` guarantee — React can't hold a
   *  component on-screen after its parent stops rendering it at all, so
   *  `minVisible` only has something to enforce when `show` toggles false
   *  while `Deferred` itself stays mounted. */
  show?: boolean;
  /** Milliseconds to wait before showing `children` at all. Kills the
   *  sub-300ms flash where a spinner appears and disappears faster than a
   *  user can register it. Default 200ms. */
  delay?: number;
  /** Once shown, `children` stays visible for at least this many ms even if
   *  `show` flips back to false first. Kills the "strobe" where a fast
   *  response makes the spinner blink on then immediately off. Default
   *  400ms. */
  minVisible?: number;
  children: ReactNode;
}

/**
 * Deferred — wraps a spinner (or any transient loading affordance) so it
 * never flashes. Renders nothing for the first `delay` ms; if `show` goes
 * false (or the component unmounts) before `delay` elapses, nothing is ever
 * shown. Once shown, it stays visible for at least `minVisible` ms even if
 * `show` resolves sooner.
 *
 * Rule 59: spinners are deferred. Skeletons (Table ghost rows,
 * EntityHeaderSkeleton, DetailPageSkeleton) do NOT need this — they're cheap
 * to render and expected during cold-cache loads. `Deferred` is for the
 * surviving spinner cases: button/mutation feedback and auth screens where a
 * skeleton doesn't apply.
 *
 * ```tsx
 * <Deferred delay={200} minVisible={400}>{spinner}</Deferred>
 * ```
 */
export function Deferred({ show = true, delay = 200, minVisible = 400, children }: DeferredProps) {
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!show) {
      setVisible(false);
      shownAtRef.current = null;
      return;
    }
    const showTimer = setTimeout(() => {
      shownAtRef.current = Date.now();
      setVisible(true);
    }, delay);
    return () => clearTimeout(showTimer);
  }, [show, delay]);

  // `show` flipped false while visible — hold it open until minVisible, then
  // hide. Runs as its own effect so the hide timer resets cleanly per show.
  useEffect(() => {
    if (show || !visible) return;
    const elapsed = shownAtRef.current ? Date.now() - shownAtRef.current : minVisible;
    const remaining = Math.max(minVisible - elapsed, 0);
    const hideTimer = setTimeout(() => setVisible(false), remaining);
    return () => clearTimeout(hideTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!visible) return null;
  return <>{children}</>;
}
